import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Department } from '@app/features/scoping/models/department.model'
import { Role } from '@app/features/scoping/models/role.model'
import { plainToInstance } from 'class-transformer'
import { ScopeSection } from '../model/scope-section'
import { getMoneyValue, mapScopeTpc } from './map-scope-entity.utils'
import { mapScopeFee } from './map-scope-fee.utils'

export const mapScopeDeliverable = (deliverable, sectionId?: number): Deliverable => {
  const section = mapDeliverableSection(deliverable, sectionId)

  return plainToInstance(Deliverable, {
    ...deliverable,
    section,
    budget: getMoneyValue(deliverable.budget, deliverable.currencyUnit),
    budgetBalance: getMoneyValue(deliverable.budgetBalance, deliverable.currencyUnit),
    fixedCostOffset: getMoneyValue(deliverable.fixedCostOffset, deliverable.currencyUnit),
    feeItems: (deliverable.feeItems || []).map((feeItem) =>
      mapScopeFee(feeItem, { scopeSection: section, deliverable })
    ),
    thirdPartyCosts: (deliverable.thirdPartyCosts || []).map((tpc) => mapScopeTpc(tpc, deliverable.currencyUnit)),
    components: (deliverable.components || []).map((component) => mapDeliverableComponent(component, deliverable)),
    sections: (deliverable.sections || []).map((section) => mapSection(section, deliverable.currencyUnit)),
  })
}

export function mapSection(section: ScopeSection, currency: string) {
  return plainToInstance(ScopeSection, {
    ...section,
    thirdPartyCosts: (section?.thirdPartyCosts || []).map((tpc: ThirdPartyCost) => mapScopeTpc(tpc, currency)),
    feeItems: (section.feeItems || []).map((feeItem) => mapScopeFee(feeItem, { scopeSection: section })),
  })
}

export function mapDeliverableSection(deliverable: Deliverable, sectionId?: number) {
  if (deliverable.section) return mapSection(deliverable.section, deliverable.currencyUnit)

  return sectionId ? plainToInstance(ScopeSection, { id: sectionId }) : null
}

export function mapDeliverableComponent(component: ScopeComponent, deliverable: Deliverable) {
  const deliverableInstance = plainToInstance(Deliverable, { id: deliverable.id, sourceType: deliverable.sourceType })

  return plainToInstance(ScopeComponent, {
    ...component,
    feeItems: (component.feeItems || []).map((feeItem) =>
      mapScopeFee(feeItem, { deliverable: deliverableInstance, component, scopeSection: deliverable.section })
    ),
    deliverable: deliverableInstance,
    departments: (component.departments || []).map((department) =>
      mapDeliverableDepartment(department, deliverable.currencyUnit)
    ),
    thirdPartyCosts: (component.thirdPartyCosts || []).map((tpc) => mapScopeTpc(tpc, deliverable.currencyUnit)),
  })
}

export function mapDeliverableDepartment(department, currency: string) {
  return plainToInstance(Department, {
    ...department,
    roles: department.roles.map((role) => mapDeliverableRole(role, currency)),
  })
}

export function mapDeliverableRole(role, currency: string) {
  return plainToInstance(Role, {
    ...role,
    rateCardRate: getMoneyValue(role.rate, currency),
    rateCardCost: getMoneyValue(role.cost, currency),
    rateCardMinutes: role.rateCardHours ? role.rateCardHours * 60 : 0,
  })
}

/** to API utils */

export function mapScopeDeliverableToPlainApi(deliverable: Deliverable) {
  if (!deliverable) return null

  return plainToInstance(Deliverable, {
    ...deliverable,
    components: null,
  })
}
